import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import type { Review, ReviewCreateInput, ReviewUpdateInput, ReviewSummary } from '../../services/reviewsService';

interface ReviewsState {
  reviews: Review[];
  summary: ReviewSummary | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReviewsState = {
  reviews: [],
  summary: null,
  loading: false,
  error: null,
};

// Transform backend response to frontend format
const transformReviewData = (backendReview: any): Review => {
  return {
    id: backendReview.id,
    user_id: backendReview.user_id,
    customer_id: backendReview.case_file?.customer_id,
    case_id: backendReview.case_id,
    client_name: backendReview.case_file ? 
      `${backendReview.case_file.customer?.first_name || ''} ${backendReview.case_file.customer?.last_name || ''}`.trim() : 
      undefined,
    case_subject: backendReview.case_file?.subject,
    case_type: backendReview.case_file?.type,
    lawyer_satisfaction: parseFloat(backendReview.lawyer_satisfaction) || 0,
    firm_satisfaction: parseFloat(backendReview.firm_satisfaction) || 0,
    recommendation: parseInt(backendReview.recommendation) || 0,
    case_file: backendReview.case_file, // Include the entire case_file object
    created_at: backendReview.created_at,
    updated_at: backendReview.updated_at,
  };
};

// Async thunks matching backend routes
export const fetchReviews = createAsyncThunk(
  'reviews/fetchReviews',
  async () => {
    const response = await api.get('/reviews');
    // Handle the backend response format
    const data = response.data;
    if (data.status && data.data) {
      // Transform the data array
      const transformedData = data.data.map(transformReviewData);
      return transformedData;
    }
    // Fallback for direct response
    const transformedData = data.map(transformReviewData);
    return transformedData;
  }
);

export const createReview = createAsyncThunk(
  'reviews/createReview',
  async (data: ReviewCreateInput) => {
    const response = await api.post('/reviews', data);
    return response.data;
  }
);

export const updateReview = createAsyncThunk(
  'reviews/updateReview',
  async ({ reviewId, data }: { reviewId: number; data: ReviewUpdateInput }) => {
    const response = await api.put(`/reviews/${reviewId}`, data);
    return response.data;
  }
);

export const deleteReview = createAsyncThunk(
  'reviews/deleteReview',
  async (reviewId: number) => {
    await api.delete(`/reviews/${reviewId}`);
    return reviewId;
  }
);

export const fetchReviewSummary = createAsyncThunk(
  'reviews/fetchReviewSummary',
  async () => {
    const response = await api.get('/reviews/summary');
    // Handle the backend response format
    const data = response.data;
    if (data.status && data.data) {
      return data.data; // Extract the actual summary data from the response
    }
    return data; // Fallback for direct response
  }
);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReviews: (state) => {
      state.reviews = [];
      state.summary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch reviews
      .addCase(fetchReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload;
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch reviews';
      })
      
      // Create review
      .addCase(createReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews.unshift(action.payload);
      })
      .addCase(createReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create review';
      })
      
      // Update review
      .addCase(updateReview.fulfilled, (state, action) => {
        const index = state.reviews.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update review';
      })
      
      // Delete review
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(r => r.id !== action.payload);
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete review';
      })
      
      // Fetch review summary
      .addCase(fetchReviewSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summary = action.payload;
      })
      .addCase(fetchReviewSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch review summary';
      });
  },
});

export const { clearError, clearReviews } = reviewsSlice.actions;
export default reviewsSlice.reducer;
